import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import { useNetworkStore } from "../stores/network";
import { useMyTxs } from "../stores/my-txs";
import { theme } from "../lib/theme";
import { Button, Mono } from "./ui";

const Wrapper = styled.div`
  position: fixed;
  bottom: ${theme.spacing.md};
  right: ${theme.spacing.md};
  z-index: 100;
`;

const Pill = styled.button<{ count: number }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 20px;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.bgCard};
  color: ${theme.colors.text};
  font-size: ${theme.fontSize.sm};
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);

  &:hover {
    border-color: ${theme.colors.primary};
  }
`;

const CountBadge = styled.span`
  background: ${theme.colors.primary};
  color: white;
  font-size: 10px;
  font-weight: 700;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Panel = styled.div`
  position: absolute;
  bottom: calc(100% + ${theme.spacing.sm});
  right: 0;
  width: 320px;
  background: ${theme.colors.bgCard};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.lg};
  padding: ${theme.spacing.md};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.xs} 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${theme.colors.border};
  }
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.textMuted};
  cursor: pointer;
  font-size: 14px;
  padding: 0 4px;
  line-height: 1;

  &:hover {
    color: ${theme.colors.danger};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.xs};
`;

export function TxClipboard() {
  const { removeFromClipboard, clearClipboard, getClipboard } = useMyTxs();
  const { selectedNetwork } = useNetworkStore();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const clipboard = getClipboard(selectedNetwork);

  if (clipboard.length === 0) return null;

  return (
    <Wrapper>
      {open && (
        <Panel>
          <div style={{ fontSize: theme.fontSize.xs, color: theme.colors.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            TX Clipboard ({clipboard.length}/5)
          </div>
          {clipboard.map((hash) => (
            <Item key={hash}>
              <Mono style={{ fontSize: "10px", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {hash.slice(0, 10)}...{hash.slice(-6)}
              </Mono>
              <RemoveBtn onClick={() => removeFromClipboard(selectedNetwork, hash)}>×</RemoveBtn>
            </Item>
          ))}
          <Actions>
            <Button
              style={{ flex: 1, padding: "6px" }}
              disabled={clipboard.length < 2}
              onClick={() => {
                setOpen(false);
                navigate(`/${selectedNetwork}/compare?txs=${clipboard.join(",")}`);
              }}
            >
              Compare
            </Button>
            <Button variant="ghost" style={{ padding: "6px 10px" }} onClick={() => clearClipboard(selectedNetwork)}>
              Clear
            </Button>
          </Actions>
        </Panel>
      )}
      <Pill count={clipboard.length} onClick={() => setOpen((o) => !o)}>
        <CountBadge>{clipboard.length}</CountBadge>
        Compare
      </Pill>
    </Wrapper>
  );
}
